import { API_BASE_URL } from '../config';

// Detect if we're in development (localhost)
const isDevelopment = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1'
);

// Production API base URL (where PHP files are hosted)
const PRODUCTION_API_URL = 'https://redhand.vippanel.in';

// Get the correct API base URL for PHP endpoints
// In development, use production URL (PHP files are on production server)
// In production, use relative paths (PHP files are on same server)
const getPHPAPIUrl = (endpoint) => {
  if (isDevelopment) {
    // Development: Use full production URL
    return `${PRODUCTION_API_URL}${endpoint}`;
  } else {
    // Production: Use relative path
    return endpoint;
  }
};

// Admin token control endpoints
export const adminAPI = {
  // Create new token with Firebase config
  createToken: async (firebaseConfig) => {
    return fetch(`${API_BASE_URL}/api/admin/tokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(firebaseConfig)
    });
  },

  // Change PIN for token
  // Uses PHP backend on shared hosting
  changePin: async (token, oldPin, newPin) => {
    try {
      // Get correct API URL (production in dev, relative in production)
      const apiPath = getPHPAPIUrl('/api/change-pin.php');
      const response = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, oldPin, newPin })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to change PIN');
      }
      
      return data;
    } catch (error) {
      console.error('API change PIN failed:', error);
      throw error;
    }
  },

  // Get all active tokens
  getTokens: async () => {
    return fetch(`${API_BASE_URL}/api/admin/tokens`);
  }
};

// Verify token exists (before PIN entry)
// Uses PHP backend on shared hosting
export const verifyToken = async (token) => {
  try {
    // Get correct API URL (production in dev, relative in production)
    const apiPath = getPHPAPIUrl('/api/verify-token.php');
    if (isDevelopment) {
      console.log('🔧 Development mode: Using production API:', apiPath);
    }
    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Token verification API error:', error);
    throw error;
  }
};

// Login API - Backend verifies token + PIN against MASTER Firebase and returns Firebase config
// Uses PHP backend on shared hosting
export const login = async (token, pin) => {
  try {
    // Get correct API URL (production in dev, relative in production)
    const apiPath = getPHPAPIUrl('/api/login.php');
    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, pin })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Login failed');
    }
    
    // Return Firebase config and other data
    return {
      success: true,
      firebaseConfig: data.firebaseConfig,
      remainingDays: data.daysRemaining || 0,
      token: data.token || token
    };
  } catch (error) {
    console.error('Login API error:', error);
    throw error;
  }
};

// Legacy function for backward compatibility (deprecated)
export const verifyTokenPin = async (token, pin) => {
  try {
    const result = await login(token, pin);
    return result.success;
  } catch (error) {
    return false;
  }
};

// Get device online/offline status
// Uses PHP backend on shared hosting
export const getDeviceStatus = async (deviceId, databaseURL) => {
  try {
    // Get correct API URL (production in dev, relative in production)
    const apiPath = getPHPAPIUrl('/api/device-status.php');
    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, databaseURL })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to get device status');
    }
    
    return {
      success: true,
      status: data.status || 'offline',
      timestamp: data.timestamp,
      deviceId: data.deviceId
    };
  } catch (error) {
    console.error('Device status API error:', error);
    throw error;
  }
};

export default adminAPI;
